import { tenantTurnoverChecklistsApi, type RentalAgreementDto, type TenantDto, type TenantTurnoverChecklistDto, type TenantTurnoverChecklistItemDto } from '../../services/api';
import { i18nService, t } from '../../services/i18n';
import { openPlaceDescriptionsDrawer } from '../tenant/place-description-dialogs';
import { PROPERTY_ICONS, escapeHtml } from './property-shared';

type TurnoverStepMeta = {
  labelKey: string;
  supportsPlaceDescription: boolean;
};

const TURNOVER_STEP_META: Record<string, TurnoverStepMeta> = {
  ExitKeysReturned: { labelKey: 'property.turnovers.step.exitKeysReturned', supportsPlaceDescription: false },
  ExitMeterReadingsRecorded: { labelKey: 'property.turnovers.step.exitMeterReadingsRecorded', supportsPlaceDescription: false },
  ExitPlaceDescriptionCompleted: { labelKey: 'property.turnovers.step.exitPlaceDescriptionCompleted', supportsPlaceDescription: true },
  ExitDepositStatusRecorded: { labelKey: 'property.turnovers.step.exitDepositStatusRecorded', supportsPlaceDescription: false },
  ExitDomicileHandled: { labelKey: 'property.turnovers.step.exitDomicileHandled', supportsPlaceDescription: false },
  EntryKeysHandedOver: { labelKey: 'property.turnovers.step.entryKeysHandedOver', supportsPlaceDescription: false },
  EntryMeterReadingsRecorded: { labelKey: 'property.turnovers.step.entryMeterReadingsRecorded', supportsPlaceDescription: false },
  EntryPlaceDescriptionCompleted: { labelKey: 'property.turnovers.step.entryPlaceDescriptionCompleted', supportsPlaceDescription: true },
  EntryDepositStatusRecorded: { labelKey: 'property.turnovers.step.entryDepositStatusRecorded', supportsPlaceDescription: false },
  EntryHouseRulesShared: { labelKey: 'property.turnovers.step.entryHouseRulesShared', supportsPlaceDescription: false },
};

export class RcPropertyTenantTurnoversTab extends HTMLElement {
  propertyId = '';
  propertyAddress = '';
  agreements: RentalAgreementDto[] = [];

  private checklists: TenantTurnoverChecklistDto[] = [];
  private loadedPropertyId = '';
  private loading = false;
  private creating = false;
  private error = '';
  private busyKey = '';

  connectedCallback(): void {
    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange as EventListener);
    this.render();
  }

  disconnectedCallback(): void {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('change', this.handleChange as EventListener);
  }

  render(): void {
    if (this.propertyId && this.loadedPropertyId !== this.propertyId && !this.loading) {
      void this.load();
    }

    const toolbar = `
      <div class="tab-toolbar turnover-toolbar">
        <div>
          <strong>${t('property.turnovers.title')}</strong>
          <p class="turnover-toolbar-copy">${t('property.turnovers.subtitle')}</p>
        </div>
        <button class="btn btn-primary btn-sm" data-action="add-turnover" ${this.creating || this.loading || this.agreements.length === 0 ? 'disabled' : ''}>
          ${PROPERTY_ICONS.plus}
          ${t('property.turnovers.addButton')}
        </button>
      </div>
    `;

    if (this.loading && this.loadedPropertyId !== this.propertyId) {
      this.innerHTML = `${toolbar}<div class="empty-state"><p>${t('common.loading')}</p></div>`;
      return;
    }

    if (this.error) {
      this.innerHTML = `${toolbar}<div class="empty-state"><p>${escapeHtml(this.error)}</p></div>`;
      return;
    }

    if (this.checklists.length === 0) {
      this.innerHTML = `${toolbar}<div class="empty-state turnover-empty"><p>${t('property.turnovers.empty')}</p></div>`;
      return;
    }

    this.innerHTML = `
      ${toolbar}
      <div class="turnover-list">
        ${this.checklists.map((checklist) => this.renderChecklist(checklist)).join('')}
      </div>
    `;
  }

  private async load(): Promise<void> {
    if (!this.propertyId) {
      return;
    }

    this.loading = true;
    this.error = '';
    this.render();

    try {
      this.checklists = await tenantTurnoverChecklistsApi.getByProperty(this.propertyId);
      this.loadedPropertyId = this.propertyId;
      this.error = '';
      this.dispatchCountChange();
    } catch (error) {
      console.error('Failed to load tenant turnover checklists:', error);
      this.error = error instanceof Error ? error.message : t('property.turnovers.loadError');
    } finally {
      this.loading = false;
      this.render();
    }
  }

  private renderChecklist(checklist: TenantTurnoverChecklistDto): string {
    const exitItems = checklist.items.filter((item) => item.phase === 'Exit');
    const entryItems = checklist.items.filter((item) => item.phase === 'Entry');
    const completedCount = checklist.items.filter((item) => item.isCompleted).length;

    return `
      <article class="turnover-card" data-checklist-id="${checklist.id}">
        <div class="turnover-card-header">
          <div>
            <h3>${escapeHtml(checklist.title)}</h3>
            <div class="turnover-card-meta">
              <span class="badge badge-info">${completedCount}/${checklist.items.length} ${t('property.turnovers.completedShort')}</span>
              <span>${escapeHtml(i18nService.formatDate(checklist.updatedAt))}</span>
            </div>
          </div>
          <button class="btn btn-danger btn-sm" data-action="delete-turnover" data-checklist-id="${checklist.id}">${t('common.delete')}</button>
        </div>
        <div class="turnover-party-grid">
          <div class="turnover-party-card">
            <span class="turnover-party-label">${t('property.turnovers.exitPhase')}</span>
            <strong>${escapeHtml(checklist.outgoingTenantName || t('property.turnovers.noOutgoingTenant'))}</strong>
          </div>
          <div class="turnover-party-card">
            <span class="turnover-party-label">${t('property.turnovers.entryPhase')}</span>
            <strong>${escapeHtml(checklist.incomingTenantName || t('property.turnovers.noIncomingTenant'))}</strong>
          </div>
        </div>
        <div class="turnover-phase-grid">
          ${this.renderPhase(checklist, 'Exit', exitItems, checklist.outgoingRentalAgreementId, checklist.outgoingTenantId)}
          ${this.renderPhase(checklist, 'Entry', entryItems, checklist.incomingRentalAgreementId, checklist.incomingTenantId)}
        </div>
      </article>
    `;
  }

  private renderPhase(
    checklist: TenantTurnoverChecklistDto,
    phase: 'Exit' | 'Entry',
    items: TenantTurnoverChecklistItemDto[],
    agreementId?: string | null,
    tenantId?: string | null,
  ): string {
    const completed = items.filter((item) => item.isCompleted).length;
    const disabled = !agreementId;

    return `
      <section class="turnover-phase-card">
        <div class="turnover-phase-header">
          <div>
            <h4>${t(phase === 'Exit' ? 'property.turnovers.exitPhase' : 'property.turnovers.entryPhase')}</h4>
            <p>${t(phase === 'Exit' ? 'property.turnovers.exitHelp' : 'property.turnovers.entryHelp')}</p>
          </div>
          <span class="badge ${completed === items.length && items.length > 0 ? 'badge-success' : 'badge-muted'}">${completed}/${items.length}</span>
        </div>
        ${disabled ? `<div class="alert alert-warning turnover-phase-warning">${t(phase === 'Exit' ? 'property.turnovers.exitMissingAgreement' : 'property.turnovers.entryMissingAgreement')}</div>` : ''}
        <div class="turnover-step-list">
          ${items.map((item) => this.renderItem(checklist.id, item, agreementId, tenantId, disabled)).join('')}
        </div>
      </section>
    `;
  }

  private renderItem(
    checklistId: string,
    item: TenantTurnoverChecklistItemDto,
    agreementId?: string | null,
    tenantId?: string | null,
    disabled = false,
  ): string {
    const meta = TURNOVER_STEP_META[item.step] ?? { labelKey: item.step, supportsPlaceDescription: false };
    const busy = this.busyKey === `${checklistId}:${item.id}`;
    const actionButton = meta.supportsPlaceDescription && agreementId && tenantId
      ? `<button class="btn btn-outline btn-sm" data-action="open-place-description" data-agreement-id="${agreementId}" data-tenant-id="${tenantId}">${t('property.turnovers.openPlaceDescription')}</button>`
      : '';

    return `
      <label class="turnover-step-row ${item.isCompleted ? 'is-complete' : ''}">
        <input
          type="checkbox"
          data-checklist-id="${checklistId}"
          data-item-id="${item.id}"
          ${item.isCompleted ? 'checked' : ''}
          ${disabled || busy ? 'disabled' : ''}>
        <div class="turnover-step-copy">
          <span class="turnover-step-label">${t(meta.labelKey)}</span>
          ${item.completedAtUtc ? `<span class="turnover-step-meta">${t('property.turnovers.completedAt')} ${escapeHtml(i18nService.formatDate(item.completedAtUtc))}</span>` : ''}
        </div>
        <div class="turnover-step-actions">
          ${actionButton}
        </div>
      </label>
    `;
  }

  private handleClick = async (event: Event): Promise<void> => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-action]');
    if (!target) {
      return;
    }

    switch (target.dataset.action) {
      case 'add-turnover':
        await this.openCreateModal();
        return;
      case 'delete-turnover':
        if (target.dataset.checklistId) {
          await this.deleteChecklist(target.dataset.checklistId);
        }
        return;
      case 'open-place-description':
        if (target.dataset.agreementId && target.dataset.tenantId) {
          await this.openPlaceDescription(target.dataset.agreementId, target.dataset.tenantId);
        }
        return;
      default:
        return;
    }
  };

  private handleChange = async (event: Event): Promise<void> => {
    const input = event.target as HTMLInputElement | null;
    if (!input || input.type !== 'checkbox' || !input.dataset.checklistId || !input.dataset.itemId) {
      return;
    }

    const checklistId = input.dataset.checklistId;
    const itemId = input.dataset.itemId;
    const checked = input.checked;
    const busyKey = `${checklistId}:${itemId}`;

    this.busyKey = busyKey;
    this.render();

    try {
      const updated = await tenantTurnoverChecklistsApi.updateItem(checklistId, itemId, { isCompleted: checked });
      this.replaceChecklist(updated);
    } catch (error) {
      console.error('Failed to update tenant turnover checklist item:', error);
      input.checked = !checked;
      this.error = error instanceof Error ? error.message : t('property.turnovers.updateError');
    } finally {
      this.busyKey = '';
      this.render();
    }
  };

  private async openCreateModal(): Promise<void> {
    if (!this.propertyId) {
      return;
    }

    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay';
    overlay.innerHTML = `
      <div class="modal">
        <div class="modal-header">
          <div>
            <h2>${t('property.turnovers.createTitle')}</h2>
            <p class="modal-subtitle">${escapeHtml(this.propertyAddress)}</p>
          </div>
          <button class="modal-close" data-action="close">${PROPERTY_ICONS.close}</button>
        </div>
        <form>
          <div class="form-error" data-form-error hidden></div>
          <div class="form-group">
            <label>${t('common.title')} *</label>
            <input name="title" required placeholder="${t('property.turnovers.titlePlaceholder')}">
          </div>
          <div class="form-group">
            <label>${t('property.turnovers.outgoingTenantLabel')}</label>
            <select name="outgoingRentalAgreementId">
              <option value="">${t('property.turnovers.noOutgoingTenant')}</option>
              ${this.getAgreementOptions().map((agreement) => `<option value="${agreement.id}">${escapeHtml(agreement.tenantName)}</option>`).join('')}
            </select>
          </div>
          <div class="form-group">
            <label>${t('property.turnovers.incomingTenantLabel')}</label>
            <select name="incomingRentalAgreementId">
              <option value="">${t('property.turnovers.noIncomingTenant')}</option>
              ${this.getAgreementOptions().map((agreement) => `<option value="${agreement.id}">${escapeHtml(agreement.tenantName)}</option>`).join('')}
            </select>
          </div>
          <div class="modal-actions">
            <button type="button" class="btn btn-outline" data-action="close">${t('common.cancel')}</button>
            <button type="submit" class="btn btn-primary">${t('common.create')}</button>
          </div>
        </form>
      </div>
    `;

    const close = (): void => overlay.remove();
    overlay.addEventListener('click', (evt) => { if (evt.target === overlay) close(); });
    overlay.querySelectorAll('[data-action="close"]').forEach((button) => button.addEventListener('click', close));
    overlay.querySelector('form')?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.target as HTMLFormElement;
      const formData = new FormData(form);
      const submitButton = form.querySelector('[type="submit"]') as HTMLButtonElement;
      const errorBox = form.querySelector<HTMLElement>('[data-form-error]');
      if (errorBox) {
        errorBox.hidden = true;
        errorBox.textContent = '';
      }
      submitButton.disabled = true;
      this.creating = true;

      try {
        const created = await tenantTurnoverChecklistsApi.create(this.propertyId, {
          title: ((formData.get('title') as string) || '').trim() || undefined,
          outgoingRentalAgreementId: ((formData.get('outgoingRentalAgreementId') as string) || '').trim() || undefined,
          incomingRentalAgreementId: ((formData.get('incomingRentalAgreementId') as string) || '').trim() || undefined,
        });
        this.checklists = [created, ...this.checklists];
        this.error = '';
        this.dispatchCountChange();
        close();
      } catch (error) {
        console.error('Failed to create tenant turnover checklist:', error);
        if (errorBox) {
          errorBox.hidden = false;
          errorBox.textContent = error instanceof Error ? error.message : t('property.turnovers.createError');
        }
        submitButton.disabled = false;
      } finally {
        this.creating = false;
        this.render();
      }
    });

    document.body.appendChild(overlay);
  }

  private async deleteChecklist(checklistId: string): Promise<void> {
    const { confirmDialog } = await import('../shared/rc-confirm-dialog');
    const confirmed = await confirmDialog(t('property.turnovers.confirmDelete'));
    if (!confirmed) {
      return;
    }

    try {
      await tenantTurnoverChecklistsApi.delete(checklistId);
      this.checklists = this.checklists.filter((item) => item.id !== checklistId);
      this.dispatchCountChange();
      this.render();
    } catch (error) {
      console.error('Failed to delete tenant turnover checklist:', error);
      this.error = error instanceof Error ? error.message : t('property.turnovers.deleteError');
      this.render();
    }
  }

  private async openPlaceDescription(agreementId: string, tenantId: string): Promise<void> {
    const agreement = this.agreements.find((item) => item.id === agreementId);
    if (!agreement) {
      return;
    }

    const tenant: TenantDto = {
      id: tenantId,
      propertyId: agreement.propertyId,
      propertyAddress: agreement.propertyAddress,
      firstName: agreement.firstName || agreement.tenantName.split(' ')[0] || agreement.tenantName,
      lastName: agreement.lastName || agreement.tenantName.split(' ').slice(1).join(' '),
      email: agreement.email,
      phone: agreement.phone,
      status: agreement.status,
      createdAt: '',
      updatedAt: '',
    };

    await openPlaceDescriptionsDrawer(tenant, this.agreements.filter((item) => item.tenantId === tenantId));
  }

  private getAgreementOptions(): RentalAgreementDto[] {
    return [...this.agreements]
      .sort((left, right) => (left.tenantName || '').localeCompare(right.tenantName || ''));
  }

  private replaceChecklist(updated: TenantTurnoverChecklistDto): void {
    this.checklists = this.checklists.map((item) => item.id === updated.id ? updated : item);
  }

  private dispatchCountChange(): void {
    this.dispatchEvent(new CustomEvent('property-turnover-count-changed', {
      bubbles: true,
      detail: { count: this.checklists.length },
    }));
  }
}

if (!customElements.get('rc-property-tenant-turnovers-tab')) {
  customElements.define('rc-property-tenant-turnovers-tab', RcPropertyTenantTurnoversTab);
}
